import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type { MessageCreateRequest } from "@/api/types";
import { AdminCreatePostFormAudienceSelector } from "@/components/AdminCreatePost/AdminCreatePostFormAudienceSelector";
import { EntityPreview } from "@/components/EntityPreview/EntityPreview";
import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { ContentBlock } from "@/design-system/ContentBlock";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import type { AdminPostAudienceType, MessageRouteParams, MessageRouteStates } from "@/helpers/community-feed";
import { buildDeepLinkUrl, type SupportedDeepLinkEntityType } from "@/helpers/deep-link";
import { canCreateAnnouncementMessage } from "@/helpers/permissions";
import { createRequiredStringRule } from "@/helpers/rules";
import { useProject } from "@/hooks/Network/useProject";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useProjectAppBrandingConfig } from "@/providers/ProjectAppBrandingConfigProvider";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const FORM_ID = "share-to-community-feed-modal-form";

type AdminShareToCommunityFeedModalFormValues = {
  title: string;
  content: string;
  audienceType: AdminPostAudienceType | undefined;
  audienceEntityId: string | undefined;
};

type AdminShareToCommunityFeedModalProps = ModalBaseProps & {
  entityType: SupportedDeepLinkEntityType;
  entityId: string;
  otherIds?: Record<string, string>;
};

export function AdminShareToCommunityFeedModal({
  isOpened,
  onOpenChange,
  entityType,
  entityId,
  otherIds,
}: AdminShareToCommunityFeedModalProps): React.ReactNode {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const slug = useSlug();
  const project = useProject();
  const hasPermission = usePermission();
  const canCreateAnnouncement = hasPermission(canCreateAnnouncementMessage);
  const { appBrandingConfig } = useProjectAppBrandingConfig();

  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: audience,
    isPending: isPendingAudience,
    isError: isErrorAudience,
  } = useQuery(
    communityFeedQueries.audience({
      query: {
        Type: canCreateAnnouncement ? "announcement" : "post",
      },
    }),
  );
  const addMessageMutation = communityFeedMutations.useAddMessage();

  const form = useForm<AdminShareToCommunityFeedModalFormValues>({
    defaultValues: {
      title: "",
      content: "",
      audienceType: undefined,
      audienceEntityId: undefined,
    },
  });
  const currAudienceType = useWatch({
    name: "audienceType",
    control: form.control,
  });
  const currAudienceEntityId = useWatch({
    name: "audienceEntityId",
    control: form.control,
  });
  const allAudienceGroups = audience ? [...audience.realEstateGroups, ...audience.interestGroups] : [];
  const currAudienceGroup =
    currAudienceType === "group" ? allAudienceGroups.find((group) => group.id === currAudienceEntityId) : undefined;
  const currAudienceProject = audience && currAudienceType === "project" ? audience.project : undefined;
  const currAudienceProjectConnection =
    audience && currAudienceType === "projectConnection"
      ? audience.projectConnections.find((connection) => connection.id === currAudienceEntityId)
      : undefined;

  useEffect(() => {
    if (isOpened) {
      form.reset();
    }
  }, [isOpened, form]);

  const getDefaultAudience: () => Pick<AdminShareToCommunityFeedModalFormValues, "audienceEntityId" | "audienceType"> =
    useCallback(() => {
      if (!audience) {
        return {
          audienceType: undefined,
          audienceEntityId: undefined,
        };
      }

      if (audience.project) {
        return {
          audienceType: "project",
          audienceEntityId: audience.project.id,
        };
      }

      const audienceGroups = [...audience.realEstateGroups, ...audience.interestGroups];
      if (audienceGroups.length === 1) {
        return {
          audienceType: "group",
          audienceEntityId: audienceGroups[0].id,
        };
      }

      return {
        audienceType: undefined,
        audienceEntityId: undefined,
      };
    }, [audience]);

  // Set default selected audience
  useEffect(() => {
    if (!audience) {
      return;
    }

    const { audienceEntityId, audienceType } = getDefaultAudience();

    form.reset({
      ...form.getValues(),
      audienceEntityId,
      audienceType,
    });
  }, [audience, form, getDefaultAudience]);

  const handleSelectAudience = (newAudienceGroupId: string | undefined, newProjectConnectionId: string | undefined) => {
    if (newAudienceGroupId) {
      form.setValue("audienceEntityId", newAudienceGroupId, {
        shouldDirty: currAudienceType !== "group" || newAudienceGroupId !== currAudienceEntityId,
      });

      if (currAudienceType !== "group") {
        form.setValue("audienceType", "group");
      }
    } else if (newProjectConnectionId) {
      form.setValue("audienceEntityId", newProjectConnectionId, {
        shouldDirty: currAudienceType !== "projectConnection" || newProjectConnectionId !== currAudienceEntityId,
      });

      if (currAudienceType !== "projectConnection") {
        form.setValue("audienceType", "projectConnection");
      }
    } else {
      form.setValue("audienceEntityId", audience?.project?.id);

      if (currAudienceType !== "project") {
        form.setValue("audienceType", "project");
      }
    }
  };

  const handleSubmit = (formValues: AdminShareToCommunityFeedModalFormValues) => {
    let deepLinkUrl: string;
    if (entityType === "document") {
      deepLinkUrl = buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
          folderId: otherIds?.folderId || "",
        },
      });
    } else if (entityType === "group") {
      deepLinkUrl = buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
          groupTypeId: otherIds?.groupTypeId || "",
        },
      });
    } else {
      deepLinkUrl = buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
        },
      });
    }

    const postType = canCreateAnnouncement ? "announcement" : "undefined";
    const data: MessageCreateRequest = {
      type: postType,
      title: formValues.title,
      content: formValues.content + " " + deepLinkUrl,
      isHiddenFromAdmins: false,
      groupId: formValues.audienceType === "group" ? formValues.audienceEntityId : undefined,
      projectConnectionId: formValues.audienceType === "projectConnection" ? formValues.audienceEntityId : undefined,
    };

    addMessageMutation.mutate(
      {
        data,
      },
      {
        onSuccess: (createdMessage) => {
          onOpenChange(false);

          const redirectUrl =
            routes.communityFeed.list({
              slug,
            }) +
            "?" +
            new URLSearchParams({
              tab: canCreateAnnouncement ? "announcements" : "all",
            } satisfies MessageRouteParams["list"]).toString();
          void navigate(redirectUrl, {
            state: { newMessageId: createdMessage.id } satisfies MessageRouteStates["list"],
          });
        },
      },
    );
  };

  return (
    <Modal.Root
      size="md"
      height={640}
      title={t("component.share-to-community-feed-modal.title")}
      isPending={isPendingAudience || isErrorAudience}
      data-testid="share-to-community-feed-modal"
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Form.Root id={FORM_ID} formMethods={form} onSubmit={handleSubmit}>
          <Stack gap="md">
            <ContentBlock width="flow" align="left">
              <EntityPreview entityId={entityId} entityType={entityType} otherIds={otherIds || {}} readOnly />
            </ContentBlock>
            <FormField label={t("component.share-to-community-feed-modal.form.title")} required hideLabel>
              <FormInput<AdminShareToCommunityFeedModalFormValues, "title">
                name="title"
                data-testid="share-to-community-feed-title"
                placeholder={t("component.share-to-community-feed-modal.form.title.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "component.share-to-community-feed-modal.form.title"),
                  },
                }}
              />
            </FormField>
            <FormField label={t("component.share-to-community-feed-modal.form.message")} hideLabel>
              <FormTextArea<AdminShareToCommunityFeedModalFormValues, "content">
                name="content"
                placeholder={t("component.share-to-community-feed-modal.form.message.placeholder")}
                size="md"
                scrollbar
              />
            </FormField>
            {!isPendingAudience && !isErrorAudience && (
              <FormField label={t("component.share-to-community-feed-modal.form.audience")} required>
                <AdminCreatePostFormAudienceSelector
                  audience={audience}
                  selectedGroup={currAudienceGroup}
                  onSelectGroup={(group) => handleSelectAudience(group.id, undefined)}
                  selectedProject={currAudienceProject}
                  selectedProjectConnection={currAudienceProjectConnection}
                  onSelectProject={() => handleSelectAudience(undefined, undefined)}
                  onSelectProjectConnection={(connection) => handleSelectAudience(undefined, connection.id)}
                />
              </FormField>
            )}
          </Stack>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions isSticky>
        <Button form={FORM_ID} type="submit" isLoading={addMessageMutation.isPending}>
          {t("component.share-to-community-feed-modal.btn.cta")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
